import { scmpPlus } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect, useMemo } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { AdSlot } from "~/components/advertisement/ad-slots/ad-slot";
import { useTopBannerAdSlot } from "~/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "~/components/advertisement/ad-slots/types";
import { AppFooter } from "~/components/app-footer";
import { FallbackLoading } from "~/components/fallback-loading";
import { useDynamicMetaTags } from "~/components/meta/hooks";
import { TopicSeo } from "~/components/topic/topic-seo";
import { trackingAtom } from "~/components/tracking";
import { data as appData } from "~/data";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { parseQueryString } from "~/lib/utils";
import type { topicPageQuery } from "~/queries/__generated__/topicPageQuery.graphql";
import type { topicTopicPageTopic$key } from "~/queries/__generated__/topicTopicPageTopic.graphql";

import {
  AdSlotWrapper,
  Container,
  Content,
  Divider,
  Header,
  PrimaryTopicContainer,
  SecondaryContainer,
} from "./styles";

const query = graphql`
  query topicPageQuery(
    $entityId: String!
    $superTopicQueueName: String!
    $sponsorArticleQueueName: String!
    $applicationIds: [String]
    $opinionAndColumnArticleTypeIds: [String]
    $after: String
    $count: Int = 30
    $scmpPlusPaywallTypeIds: [String]
  ) {
    topic(filter: { entityId: $entityId }) {
      entityId
      types
      advertZone
      sponsor {
        __typename
      }
      entityUuid
      entityId
      sponsor {
        name
      }
      name
      urlAlias
      ...hooksUseDynamicMetaTags
      ...topicContentListTopic
        @arguments(
          applicationIds: $applicationIds
          after: $after
          count: $count
          scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        )
      ...topicTopicPageTopic
        @arguments(
          applicationIds: $applicationIds
          after: $after
          count: $count
          scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        )
      ...topicContentListOpinion
        @arguments(
          applicationIds: $applicationIds
          opinionAndColumnArticleTypeIds: $opinionAndColumnArticleTypeIds
          scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        )
      ...topicHeaderTopic
      ...topicSeoTopic
    }
    ...topicContentListTopicSuperTopicQueueQuery
      @arguments(entityId: $entityId, superTopicQueueName: $superTopicQueueName)
    ...topicSponsorArticlesQueueQuery @arguments(sponsorArticleQueueName: $sponsorArticleQueueName)
  }
`;

const TopicPage: RelayComponent<unknown, topicPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const isOpinionType = ((data.topic?.types ?? []) as string[]).includes("opinion");
  const hasSponsor = !!data.topic.sponsor;
  const hideSecondaryContainer = isOpinionType || hasSponsor;

  const { data: contentList, hasNext: hasNextPage } = usePaginationFragment<
    topicPageQuery,
    topicTopicPageTopic$key
  >(
    graphql`
      fragment topicTopicPageTopic on Topic
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
        scmpPlusPaywallTypeIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "topicTopicPagePaginationQuery") {
        contents(
          after: $after
          exclude: { paywallTypeIds: $scmpPlusPaywallTypeIds }
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "topicTopicPage__author_contents") {
          edges {
            __typename
          }
        }
      }
    `,
    data.topic,
  );

  useTopBannerAdSlot({
    desktop: null,
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.Free,
        sctid: data.topic.entityId,
      },
      zone: data.topic.advertZone,
    },
  });

  const isShowFooter = useMemo(
    () =>
      // this is to prevent eslint throw warning relay/unused-fields
      !contentList.contents?.edges || !hasNextPage,
    [contentList.contents?.edges, hasNextPage],
  );

  // Prevent send page view in SEO component as Plus will use same SEO component
  const metaTags = useDynamicMetaTags(data.topic);
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    const { entityId, entityUuid, name, sponsor, urlAlias } = data.topic;
    trackPageView?.({
      contentType: "Topic",
      entityId,
      entityUuid,
      page: "Topic",
      pageTitle: metaTags?.controlled.title ?? name,
      path: urlAlias,
      sponsor: sponsor?.name,
      topicID: entityId,
      topics: name,
    });
  }, [trackPageView, metaTags?.controlled.title, data.topic]);

  return (
    <>
      <TopicSeo reference={data.topic} />
      <Container>
        <Header reference={data.topic} />
        <Content>
          {/** Remarks: component order is for preventing layout shift */}
          <AdSlotWrapper>
            <AdSlot
              adUnit="d_lrec2"
              sizes={[[300, 250], "fluid"]}
              targeting={{
                paid: TargetingPaidType.Free,
                sctid: data.topic.entityId,
              }}
              zone={data.topic.advertZone}
            />
          </AdSlotWrapper>
          {!hideSecondaryContainer && <SecondaryContainer reference={data.topic} />}
          <Divider />
          <PrimaryTopicContainer
            showColumnAndOpinionArticles={isOpinionType}
            sponsorArticleReference={data}
            superTopicReference={data}
            topicReference={data.topic}
          />
        </Content>
      </Container>
      {isShowFooter && <AppFooter />}
    </>
  );
};

TopicPage.displayName = "TopicPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    hasDesktopAd: false,
    hasMobileAd: true,
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 0,
      tabletUp: 0,
    },
  },
};

export default withRelay(TopicPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";
    return {
      applicationIds: [appData.application.scmp.entityUuid],
      entityId,
      opinionAndColumnArticleTypeIds: [
        appData.articleTypeEntityUuids.Column,
        appData.articleTypeEntityUuids.Opinion,
      ],
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
      sponsorArticleQueueName: `topic_sponsor_articles_${entityId}`,
      superTopicQueueName: `topic_super_topic_${entityId}`,
    };
  },
});
